/**
 * PM-5 — Import Turbines wizard (steps 1–4). Opens from the "Import from
 * Excel or CSV" action on the Create Project page (PM-4); presents an
 * editable Excel-like preview grid (the centerpiece UX, PM-D6). Steps 1–4
 * of the five-step flow; commit + report is PM-6.
 *
 * Models the shipped 4-step `features/ai-findings-import/ImportAiFindingsPage`
 * (MUI Stepper + lifted parent state + analyze mutation). The wizard renders
 * inside a full-screen Dialog overlaid on the Create Project page so the
 * import "runs in the context of the project being created" (spec) without
 * navigating away.
 *
 * ── Lifted state (the PM-6 contract) ──────────────────────────────────────
 * Everything PM-6's `POST /imports/commit` body needs lives here, so PM-6
 * commits EXACTLY what the user previewed (PM-D6 — edits travel in the body,
 * not a re-upload). On finish, the wizard hands the parent an
 * `ImportWizardResult`:
 *   - `analysisToken`  — echoes the server parse (avoids re-upload).
 *   - `sheetName` / `headerRow` — the chosen sheet + header.
 *   - `mapping`        — the final (possibly overridden) column→field map.
 *   - `rows`           — the edited `ImportAnalyzedRow[]` (edited cells +
 *                        per-row `included`), the canonical preview state.
 *   - `modelResolutions` — confirmed create-new-model decisions (PM-D11).
 * That object is a ready-to-send `CommitImportBody` minus `project` /
 * `projectId` / `allowOverride`, which PM-6 adds from the create-project form
 * + the tenant_admin override path.
 */
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  useAnalyzeUploadMutation,
  useRevalidateMutation,
} from "./useImportAnalyze.js";
import { useHasRole } from "@aeriosiq/auth-client";
import { Step1Upload } from "./Step1Upload.js";
import { DataGridStep } from "./DataGridStep.js";
import { ReviewTree } from "./ReviewTree.js";
import { Step5Commit } from "./Step5Commit.js";
import { backfillRowCellsForMapping } from "./mapping.js";
import { overrideFieldKey, parseOverrideFieldKey } from "./overrideDiff.js";
import type {
  AnalyzeImportReport,
  CommitImportBody,
  ImportAnalyzedRow,
  ImportColumnMapping,
  ImportModelResolution,
  RevalidateImportBody,
} from "./types.js";

// Importer live-grid rework: collapse 5 steps → 3. The Data step is ONE live
// grid (sheet selector + click-to-set-header + per-column field dropdown +
// inline edit + live validation); Review is a parks→turbines status tree above
// the (unchanged) commit action.
const STEPS = ["Upload", "Data", "Review & import"] as const;

/** What the wizard assembled from the Upload + Data steps (the ready-to-commit
 *  lifted state). PM-6 adds `project`/`projectId` + `allowOverride`. */
export interface ImportWizardResult {
  analysisToken: string | null;
  sheetName: string;
  headerRow: number;
  mapping: ImportColumnMapping[];
  rows: ImportAnalyzedRow[];
  modelResolutions: ImportModelResolution[];
}

/** The project context the import runs in (PM-D12) — supplied by the
 *  Create Project page. Exactly one of `project` (create a new project as
 *  part of the import) / `projectId` (import into an existing one) is set. */
export interface ImportCommitContext {
  /** `CreateProjectRequest` shape — built from the create-project form. */
  project?: unknown;
  projectId?: string | null;
  /** PM-WF-01 — display label of `project.workflow` (created-with-import),
   *  resolved by the create wizard from the tenant's workflow list. */
  projectWorkflowLabel?: string;
}

export interface ImportTurbinesWizardProps {
  open: boolean;
  onClose: () => void;
  /** The project context (new project body or existing projectId) the commit
   *  runs in (PM-D12). The wizard merges it into the commit body at step 5. */
  commitContext: ImportCommitContext;
  /** Fired when the staged commit reaches a terminal status — the host can
   *  refresh lists / navigate. */
  onCommitted?: (importId: string) => void;
}

function errMsg(e: unknown): string {
  if (e && typeof e === "object" && "body" in e) {
    const body = (e as { body?: { detail?: string; message?: string } }).body;
    const detail = body?.detail ?? body?.message;
    if (detail) return detail;
  }
  if (e && typeof e === "object" && "message" in e) {
    return String(e.message);
  }
  return "Something went wrong analyzing the workbook.";
}

export function ImportTurbinesWizard({
  open,
  onClose,
  commitContext,
  onCommitted,
}: ImportTurbinesWizardProps): ReactElement {
  const [activeStep, setActiveStep] = useState(0);
  // FB-30/FB-32 — only a tenant_admin can opt cells into overriding base data
  // (the per-cell "Update Base Data" action). Same role primitive Step5Commit
  // uses.
  const isTenantAdmin = useHasRole("tenant_admin");

  // ── Lifted state ─────────────────────────────────────────────────────
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<AnalyzeImportReport | null>(null);
  const [sheetName, setSheetName] = useState<string>("");
  const [headerRow, setHeaderRow] = useState<number>(0);
  const [modelResolutions, setModelResolutions] = useState<
    Record<string, ImportModelResolution>
  >({});
  // FB-32 — the `(rowIndex, field)` CELLS the admin marked to override (update
  // base data from the sheet), keyed `${rowIndex}:${field}`. Travels into the
  // commit body's `overrideFields`; the BE overrides ONLY these (row, field)
  // base-data values — un-marked diff fields keep existing base data (FB-28).
  // Per-CELL, not per-row: marking the Park cell leaves the turbine-type cell
  // untouched.
  const [overrideFieldKeys, setOverrideFieldKeys] = useState<Set<string>>(
    () => new Set<string>(),
  );
  // Once a commit is in-flight/done (Step5Commit reports it) the Review-step
  // Back button locks — you cannot go back to the grid mid/post-import (FB-25).
  const [committing, setCommitting] = useState(false);

  const analyzeUpload = useAnalyzeUploadMutation();
  const revalidate = useRevalidateMutation();

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetState = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setReport(null);
    setSheetName("");
    setHeaderRow(0);
    setModelResolutions({});
    setOverrideFieldKeys(new Set<string>());
    setCommitting(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  function handleClose(): void {
    resetState();
    onClose();
  }

  // ── Step 1: upload + first-pass analyze ──────────────────────────────
  const handlePickFile = useCallback(
    (picked: File) => {
      setFile(picked);
      setReport(null);
      analyzeUpload.mutate(
        { file: picked },
        {
          onSuccess: (data) => {
            setReport(data);
            setSheetName(data.suggestedSheetName);
            setHeaderRow(data.suggestedHeaderRow ?? 0);
          },
        },
      );
    },
    [analyzeUpload],
  );

  // ── Re-validate (JSON, no re-upload, PM-D6) ──────────────────────────
  const runRevalidate = useCallback(
    (overrides: {
      sheetName?: string;
      headerRow?: number;
      mapping?: ImportColumnMapping[];
      rows?: ImportAnalyzedRow[];
    }) => {
      if (report === null) return;
      const body: RevalidateImportBody = {
        analysisToken: report.analysisToken ?? null,
        sheetName: overrides.sheetName ?? sheetName,
        headerRow: overrides.headerRow ?? headerRow,
        mapping: overrides.mapping ?? report.mapping,
        rows: overrides.rows ?? report.rows,
      };
      revalidate.mutate(body, {
        onSuccess: (data) => {
          // Revalidate does NOT re-parse the workbook, so it OMITS the raw
          // sheet grid (`rawRows`/`rawColumnCount`). Carry forward the last
          // parse's raw content so the Data grid keeps rendering the sheet
          // through edit/mapping/include revalidations.
          setReport((prev) => {
            const merged: AnalyzeImportReport = { ...data };
            const rawRows = data.rawRows ?? prev?.rawRows;
            const rawColumnCount = data.rawColumnCount ?? prev?.rawColumnCount;
            if (rawRows !== undefined) merged.rawRows = rawRows;
            if (rawColumnCount !== undefined) {
              merged.rawColumnCount = rawColumnCount;
            }
            return merged;
          });
          // The server may re-suggest a header for a freshly-picked sheet.
          if (overrides.sheetName !== undefined) {
            setHeaderRow(data.suggestedHeaderRow ?? overrides.headerRow ?? 0);
          }
        },
      });
    },
    [report, sheetName, headerRow, revalidate],
  );

  const debouncedRevalidate = useCallback(
    (overrides: Parameters<typeof runRevalidate>[0]) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        runRevalidate(overrides);
      }, 300);
    },
    [runRevalidate],
  );

  // ── Re-analyze a different sheet / header row ────────────────────────
  // The analyze response only carries the ACTIVE sheet's cells, and the BE
  // analyze is stateless (the JSON re-validate path only re-checks the rows it
  // is given — it cannot re-parse a different sheet/header). So switching sheet
  // or header row must re-parse the workbook server-side. We re-POST the File
  // the browser already holds (no user re-selection — the PM-D6 "no re-upload"
  // promise is about not making the user re-pick the file, which holds here);
  // edited grid values still travel via runRevalidate, never a re-parse.
  const runReanalyze = useCallback(
    (opts: { sheetName: string; headerRow?: number }) => {
      if (file === null) return;
      analyzeUpload.mutate(
        opts.headerRow === undefined
          ? { file, sheetName: opts.sheetName }
          : { file, sheetName: opts.sheetName, headerRow: opts.headerRow },
        {
          onSuccess: (data) => {
            setReport(data);
            // Keep the user's EXPLICIT choices — the BE echoes back the
            // auto-suggested sheet/header (not the chosen ones). On a sheet
            // switch (headerRow omitted) adopt the new sheet's suggested header.
            setSheetName(opts.sheetName);
            setHeaderRow(opts.headerRow ?? data.suggestedHeaderRow ?? 0);
          },
        },
      );
    },
    [file, analyzeUpload],
  );

  // ── Step 2 handlers ──────────────────────────────────────────────────
  function handleChangeSheet(next: string): void {
    setSheetName(next);
    // New sheet → let the BE re-suggest that sheet's header row (omit headerRow).
    runReanalyze({ sheetName: next });
  }
  function handleChangeHeaderRow(next: number): void {
    setHeaderRow(next);
    runReanalyze({ sheetName, headerRow: next });
  }

  // ── Step 3 handlers ──────────────────────────────────────────────────
  function handleChangeMapping(next: ImportColumnMapping[]): void {
    // FB-31 — when the user maps a previously unmapped/ignored raw column, the
    // analyzed rows carry no cell for it (the BE only emitted cells for the
    // columns mapped at parse time). Backfill each row's cells from the raw
    // sheet for every column the NEW mapping targets, so the newly-mapped
    // column's data reaches the re-validate (and the Review tree picks up the
    // new field). Existing cells — including inline edits — are preserved.
    let nextRows: ImportAnalyzedRow[] = [];
    setReport((prev) => {
      if (!prev) return prev;
      nextRows = backfillRowCellsForMapping(prev.rows, prev.rawRows, next);
      return { ...prev, mapping: next, rows: nextRows };
    });
    // Send the backfilled rows explicitly — `runRevalidate` otherwise falls
    // back to the stale `report.rows` (which still lack the new column's cells).
    debouncedRevalidate({ mapping: next, rows: nextRows });
  }
  function handleConfirmModel(resolution: ImportModelResolution): void {
    setModelResolutions((prev) => ({
      ...prev,
      [resolution.rawType]: resolution,
    }));
  }
  // FB-32 — toggle a single CELL's override-marked state (the clicked yellow
  // cell's `(rowIndex, field)`), NOT the whole row. Guarded by the role gate
  // (the action is only offered to tenant admins; the BE re-enforces too).
  function handleToggleOverrideField(rowIndex: number, field: string): void {
    if (!isTenantAdmin) return;
    const key = overrideFieldKey(rowIndex, field);
    setOverrideFieldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // ── Step 4 handlers ──────────────────────────────────────────────────
  function handleEditCell(
    rowIndex: number,
    columnIndex: number,
    value: string,
  ): void {
    // Compute the next rows ONCE and feed them to BOTH the lifted-state update
    // and the re-validate. Passing the edited rows to `debouncedRevalidate`
    // explicitly avoids the stale-closure revert: `runRevalidate` falls back to
    // the (stale) `report.rows` when `overrides.rows` is omitted, which would
    // round-trip the PRE-edit cells and overwrite the edit (the revert bug).
    let nextRows: ImportAnalyzedRow[] = [];
    setReport((prev) => {
      if (!prev) return prev;
      nextRows = prev.rows.map((r) =>
        r.rowIndex === rowIndex
          ? {
              ...r,
              cells: r.cells.some((c) => c.columnIndex === columnIndex)
                ? r.cells.map((c) =>
                    c.columnIndex === columnIndex ? { ...c, value } : c,
                  )
                : [...r.cells, { columnIndex, value }],
            }
          : r,
      );
      return { ...prev, rows: nextRows };
    });
    // Re-validate with the edited rows (debounced).
    debouncedRevalidate({ rows: nextRows });
  }
  function handleChangeIncluded(includedRowIndexes: number[]): void {
    const set = new Set(includedRowIndexes);
    let nextRows: ImportAnalyzedRow[] = [];
    setReport((prev) => {
      if (!prev) return prev;
      nextRows = prev.rows.map((r) => ({
        ...r,
        included: set.has(r.rowIndex),
      }));
      return { ...prev, rows: nextRows };
    });
    debouncedRevalidate({ rows: nextRows });
  }

  // ── Navigation ───────────────────────────────────────────────────────
  const canAdvance = useMemo(() => {
    // Upload → Data: a parse must have resolved.
    if (activeStep === 0) return report !== null && !analyzeUpload.isPending;
    // Data → Review: parsed + sheet chosen + no parse/revalidate in flight.
    // The hard-error commit gate lives inside Step5Commit (AC-1).
    if (activeStep === 1) {
      return (
        report !== null &&
        sheetName.length > 0 &&
        !analyzeUpload.isPending &&
        !revalidate.isPending
      );
    }
    return false;
  }, [
    activeStep,
    report,
    analyzeUpload.isPending,
    revalidate.isPending,
    sheetName,
  ]);

  // PM-WF-01 — the new project's workflow (created-with-import only).
  const newProjectWorkflow = useMemo<string | null>(() => {
    if (commitContext.projectId) return null;
    const w = (commitContext.project as { workflow?: unknown } | undefined)
      ?.workflow;
    return typeof w === "string" && w.length > 0 ? w : null;
  }, [commitContext]);

  // ── PM-6 — the ready-to-commit body (lifted state + project context). The
  // edited grid travels in the body (PM-D6); `allowOverride` is added inside
  // Step5Commit from the override path. ─────────────────────────────────
  const commitBodyBase = useMemo<
    Omit<CommitImportBody, "allowOverride">
  >(() => {
    const base: Omit<CommitImportBody, "allowOverride"> = {
      analysisToken: report?.analysisToken ?? null,
      sheetName,
      headerRow,
      mapping: report?.mapping ?? [],
      rows: report?.rows ?? [],
      modelResolutions: Object.values(modelResolutions),
    };
    // FB-32 — the granular per-CELL override list (tenant_admin only). The BE
    // overrides ONLY these (row, field) base-data values; every other diff
    // field keeps existing. Non-admins never accumulate any (the action is
    // gated), so this is empty. Sorted (rowIndex, then field) for a stable body.
    if (isTenantAdmin && overrideFieldKeys.size > 0) {
      const fields: { rowIndex: number; field: string }[] = [];
      for (const key of overrideFieldKeys) {
        const parsed = parseOverrideFieldKey(key);
        if (parsed !== null) fields.push(parsed);
      }
      fields.sort(
        (a, b) => a.rowIndex - b.rowIndex || a.field.localeCompare(b.field),
      );
      if (fields.length > 0) base.overrideFields = fields;
    }
    if (commitContext.projectId) {
      base.projectId = commitContext.projectId;
    } else if (commitContext.project !== undefined) {
      base.project = commitContext.project;
    }
    return base;
  }, [
    report,
    sheetName,
    headerRow,
    modelResolutions,
    overrideFieldKeys,
    isTenantAdmin,
    commitContext,
  ]);

  function handleCommitted(importId: string): void {
    onCommitted?.(importId);
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      aria-labelledby="import-turbines-heading"
    >
      <DialogTitle id="import-turbines-heading">
        Import turbines from Excel or CSV
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3, maxWidth: 720 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ minHeight: 360 }}>
          {activeStep === 0 && (
            <Step1Upload
              file={file}
              onPickFile={handlePickFile}
              isAnalyzing={analyzeUpload.isPending}
              error={analyzeUpload.isError ? errMsg(analyzeUpload.error) : null}
            />
          )}
          {activeStep === 1 && report !== null && (
            <DataGridStep
              report={report}
              sheetName={sheetName}
              headerRow={headerRow}
              isBusy={analyzeUpload.isPending || revalidate.isPending}
              modelResolutions={modelResolutions}
              isTenantAdmin={isTenantAdmin}
              overrideFieldKeys={overrideFieldKeys}
              onToggleOverrideField={handleToggleOverrideField}
              onChangeSheet={handleChangeSheet}
              onChangeHeaderRow={handleChangeHeaderRow}
              onChangeMapping={handleChangeMapping}
              onEditCell={handleEditCell}
              onChangeIncluded={handleChangeIncluded}
              onConfirmModel={handleConfirmModel}
            />
          )}
          {activeStep === 2 &&
            report !== null &&
            newProjectWorkflow !== null && (
              // PM-WF-01 — created-with-import: the new project's workflow
              // (required by projects/v1 2.25.0) travels in the commit's
              // `project` object; it is the create wizard's Workflow field.
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
                data-testid="import-project-workflow"
              >
                The new project is created with the workflow{" "}
                <strong>
                  {commitContext.projectWorkflowLabel ?? newProjectWorkflow}
                </strong>{" "}
                (change it on the project&apos;s Basics step).
              </Typography>
            )}
          {activeStep === 2 && report !== null && (
            <ReviewTree report={report} overrideFieldKeys={overrideFieldKeys}>
              <Step5Commit
                report={report}
                commitBodyBase={commitBodyBase}
                onDone={handleCommitted}
                onCommittingChange={setCommitting}
              />
            </ReviewTree>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Button onClick={handleClose}>
          {activeStep === STEPS.length - 1 ? "Close" : "Cancel"}
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => {
              setActiveStep((s) => Math.max(0, s - 1));
            }}
            // FB-25: Back is live on the Review step (pre-commit) so the user
            // can return to the Data grid; it locks only on step 0 or once a
            // commit is in-flight/done (no going back mid/post-import).
            disabled={activeStep === 0 || committing}
          >
            Back
          </Button>
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={() => {
                setActiveStep((s) => s + 1);
              }}
              disabled={!canAdvance}
            >
              Next
            </Button>
          ) : null}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
